/**
 * Board Service - Supabase Integration
 *
 * Replaces mock data with real Supabase database operations.
 * Provides CRUD operations for boards, groups, and tasks.
 */
import { supabase } from '../lib/supabase';
import { createEmptyBoard, createEmptyGroup, createEmptyTask, createEmptyComment } from '../types/board.types';
import { flexiBoardTemplateService } from './flexiBoardTemplate.service';
/**
 * Get default workspace ID (temporary until user workspaces are implemented)
 */
async function getDefaultWorkspaceId() {
    const { data: workspaces, error } = await supabase
        .from('workspaces')
        .select('id')
        .limit(1);
    if (error || !workspaces || workspaces.length === 0) {
        throw new Error('No workspace found. Please create a workspace first.');
    }
    return workspaces[0].id;
}
class BoardSupabaseService {
    flexiBoards = new Map();
    /**
     * Transform Supabase board row to COWBoard type
     */
    transformBoardRow(row) {
        return {
            id: row.id,
            title: row.title,
            description: row.description || '',
            isStarred: row.is_starred || false,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at),
            createdBy: {
                id: row.created_by,
                name: 'User', // TODO: Fetch from profiles table
                avatar: ''
            },
            members: row.members?.map((m) => ({
                id: m.user_id,
                name: m.user_name,
                avatar: m.user_avatar || ''
            })) || [],
            groups: row.groups?.map(this.transformGroupRow) || [],
            activities: row.activities?.map(this.transformActivityRow) || [],
            labels: row.labels || [],
            columnOrder: row.column_order || [
                'assignee-picker', 'status-picker', 'priority-picker', 'date-picker', 'progress-picker'
            ],
            availableColumns: row.available_columns || [
                'assignee-picker', 'status-picker', 'priority-picker', 'date-picker',
                'number-picker', 'file-picker', 'progress-picker', 'updated-picker'
            ],
            viewType: row.view_type || 'table'
        };
    }
    /**
     * Transform Supabase group row to COWBoardGroup type
     */
    transformGroupRow(row) {
        const tasks = row.tasks || [];
        return {
            id: row.id,
            title: row.title,
            color: row.color || '#579bfc',
            position: row.position || 0,
            tasks: tasks.map((t) => ({
                id: t.id,
                title: t.title,
                status: t.status || 'Not Started',
                priority: t.priority || 'Medium',
                dueDate: t.due_date ? new Date(t.due_date).getTime() : undefined,
                assigneeIds: t.assignee_ids || [],
                progress: t.progress || 0,
                updatedBy: {
                    date: new Date(t.updated_at).getTime(),
                    userId: t.updated_by_user_id,
                    userAvatar: ''
                },
                comments: t.comments?.map((c) => ({
                    id: c.id,
                    content: c.content,
                    authorId: c.author_id,
                    authorName: c.author_name,
                    authorAvatar: c.author_avatar || '',
                    createdAt: new Date(c.created_at).getTime(),
                    isEdited: !!c.edited_at,
                    style: c.style || {}
                })) || [],
                customFields: t.custom_fields || {}
            })) || []
        };
    }
    /**
     * Transform Supabase activity row to BoardActivity type
     */
    transformActivityRow(row) {
        return {
            id: row.id,
            type: row.type,
            taskId: row.task_id,
            taskTitle: row.task_title,
            userId: row.user_id,
            userName: row.user_name,
            userAvatar: row.user_avatar,
            timestamp: new Date(row.timestamp).getTime(),
            changes: row.changes
        };
    }
    // ============================================================================
    // BOARD CRUD OPERATIONS
    // ============================================================================
    /**
     * Get all boards with optional filtering
     */
    async getBoards(filter) {
        try {
            let query = supabase
                .from('boards')
                .select(`
          *,
          groups:board_groups(
            *,
            tasks(
              *,
              comments:task_comments(*)
            )
          ),
          labels:board_labels(*),
          members:board_members(*),
          activities:board_activities(*)
        `)
                .order('created_at', { ascending: false });
            // Apply title filter
            if (filter?.title) {
                query = query.ilike('title', `%${filter.title}%`);
            }
            const { data, error } = await query;
            if (error) {
                console.error('[BoardService] Error fetching boards:', error);
                throw new Error(error.message);
            }
            return (data || []).map(row => this.transformBoardRow(row));
        }
        catch (error) {
            console.error('[BoardService] Failed to fetch boards:', error);
            return [];
        }
    }
    /**
     * Get board by ID with full nested data
     */
    async getBoardById(boardId) {
        try {
            const { data, error } = await supabase
                .from('boards')
                .select(`
          id,
          title,
          description,
          is_starred,
          created_by,
          created_at,
          updated_at,
          view_type,
          folder_id,
          board_type,
          settings,
          column_order,
          available_columns,
          columns_config,
          groups:board_groups(
            *,
            tasks:board_items(
              *,
              comments:board_item_comments(*)
            )
          ),
          labels:board_labels(*),
          activities:board_activities(*)
        `)
                .eq('id', boardId)
                .single();
            if (error) {
                console.error('[BoardService] Error fetching board:', error);
                return null;
            }
            if (!data) {
                console.warn('[BoardService] No data returned for board:', boardId);
                return null;
            }
            console.log('[BoardService] Raw board data from Supabase:', data);
            const transformed = this.transformBoardRow(data);
            console.log('[BoardService] Transformed board:', transformed);
            return transformed;
        }
        catch (error) {
            console.error('[BoardService] Failed to fetch board:', error);
            return null;
        }
    }
    /**
     * Create a new board
     */
    async createBoard(boardData, createdBy) {
        try {
            // Use provided workspace ID or get default
            const workspaceId = boardData.workspaceId || await getDefaultWorkspaceId();
            let newBoard;
            // If we have management type info, create both COWBoard and FlexiBoard
            if (boardData.managementType && boardData.title) {
                // Create FlexiBoard with engine
                const { board: flexiBoard, engine } = flexiBoardTemplateService.createFlexiBoardFromTemplate({
                    name: boardData.title,
                    privacy: boardData.privacy || 'main',
                    managementType: boardData.managementType,
                    customManagementType: boardData.customManagementType
                }, createdBy);
                // Store FlexiBoard and engine
                this.flexiBoards.set(flexiBoard.id, { board: flexiBoard, engine });
                // Create board in Supabase
                const { data: boardRow, error } = await supabase
                    .from('boards')
                    .insert({
                    id: flexiBoard.id,
                    workspace_id: workspaceId,
                    folder_id: boardData.folderId || null, // Save folder association
                    title: boardData.title,
                    description: boardData.description || '',
                    is_starred: boardData.isStarred || false,
                    created_by: createdBy.id,
                    column_order: boardData.columnOrder || [
                        'assignee-picker', 'status-picker', 'priority-picker', 'date-picker'
                    ],
                    view_type: boardData.viewType || 'table'
                })
                    .select()
                    .single();
                if (error) {
                    console.error('[BoardService] Error creating board:', error);
                    throw new Error(error.message);
                }
                newBoard = this.transformBoardRow(boardRow);
            }
            else {
                // Fallback to simple board creation
                const { data: boardRow, error } = await supabase
                    .from('boards')
                    .insert({
                    workspace_id: workspaceId,
                    folder_id: boardData.folderId || null, // Save folder association
                    title: boardData.title || 'Untitled Board',
                    description: boardData.description || '',
                    is_starred: boardData.isStarred || false,
                    created_by: createdBy.id,
                    column_order: boardData.columnOrder || [
                        'assignee-picker', 'status-picker', 'priority-picker', 'date-picker'
                    ],
                    view_type: boardData.viewType || 'table'
                })
                    .select()
                    .single();
                if (error) {
                    console.error('[BoardService] Error creating board:', error);
                    throw new Error(error.message);
                }
                newBoard = this.transformBoardRow(boardRow);
            }
            // Add creator as board owner
            try {
                await this.addBoardMember(newBoard.id, createdBy.id, createdBy.name, createdBy.avatar || null, 'owner');
            }
            catch (memberError) {
                console.warn('[BoardService] Failed to add creator as board member:', memberError);
            }
            // Create default groups for the new board
            const createdGroupIds = [];
            try {
                const defaultGroups = ['Todo', 'In Progress', 'Done'];
                const colors = ['#579bfc', '#ff9800', '#00c875'];
                for (let i = 0; i < defaultGroups.length; i++) {
                    const group = await this.createGroup(newBoard.id, {
                        title: defaultGroups[i],
                        color: colors[i],
                        position: i,
                        isCollapsed: false
                    });
                    if (group?.id) {
                        createdGroupIds.push(group.id);
                    }
                }
            }
            catch (groupError) {
                console.warn('[BoardService] Failed to create default groups:', groupError);
                // Don't throw - board was created successfully
            }
            // Create default items in first group
            try {
                if (createdGroupIds.length > 0) {
                    const defaultItems = ['Welcome to your board!', 'Add your first task', 'Edit and organize'];
                    for (let i = 0; i < defaultItems.length; i++) {
                        await supabase
                            .from('tasks')
                            .insert({
                            board_id: newBoard.id,
                            group_id: createdGroupIds[0],
                            title: defaultItems[i],
                            status: 'Not Started',
                            position: i,
                            updated_by_user_id: createdBy.id
                        })
                            .select();
                    }
                }
            }
            catch (itemError) {
                console.warn('[BoardService] Failed to create default items:', itemError);
                // Don't throw - board was created successfully
            }
            return newBoard;
        }
        catch (error) {
            console.error('[BoardService] Failed to create board:', error);
            throw error;
        }
    }
    /**
     * Update board properties
     */
    async updateBoard(boardId, updates) {
        try {
            const { data, error } = await supabase
                .from('boards')
                .update({
                title: updates.title,
                description: updates.description,
                is_starred: updates.isStarred,
                column_order: updates.columnOrder,
                view_type: updates.viewType
            })
                .eq('id', boardId)
                .select()
                .single();
            if (error) {
                console.error('[BoardService] Error updating board:', error);
                return null;
            }
            return this.transformBoardRow(data);
        }
        catch (error) {
            console.error('[BoardService] Failed to update board:', error);
            return null;
        }
    }
    /**
     * Delete board
     */
    async deleteBoard(boardId) {
        try {
            const { error } = await supabase
                .from('boards')
                .delete()
                .eq('id', boardId);
            if (error) {
                console.error('[BoardService] Error deleting board:', error);
                return false;
            }
            // Remove from FlexiBoards cache
            this.flexiBoards.delete(boardId);
            return true;
        }
        catch (error) {
            console.error('[BoardService] Failed to delete board:', error);
            return false;
        }
    }
    /**
     * Toggle board star status
     */
    async toggleBoardStar(boardId) {
        const board = await this.getBoardById(boardId);
        if (!board)
            return null;
        return this.updateBoard(boardId, { isStarred: !board.isStarred });
    }
    // ============================================================================
    // GROUP OPERATIONS (Future - will implement in next phase)
    // ============================================================================
    async createGroup(boardId, groupData) {
        try {
            // Get existing groups count to generate sequential name
            const { data: existingGroups, error: countError } = await supabase
                .from('board_groups')
                .select('id')
                .eq('board_id', boardId);
            if (countError) {
                console.error('Error counting groups:', countError);
            }
            const groupCount = existingGroups?.length || 0;
            const defaultTitle = `Group ${groupCount + 1}`;
            const groupInsert = {
                board_id: boardId,
                title: groupData.title || defaultTitle,
                color: groupData.color || '#579bfc',
                position: groupData.position !== undefined ? groupData.position : groupCount
            };
            const { data, error } = await supabase
                .from('board_groups')
                .insert(groupInsert)
                .select()
                .single();
            if (error) {
                console.error('Error creating group:', error);
                return null;
            }
            return {
                id: data.id,
                title: data.title,
                color: data.color,
                position: data.position,
                tasks: []
            };
        }
        catch (error) {
            console.error('Error in createGroup:', error);
            return null;
        }
    }
    async updateGroup(boardId, groupId, updates) {
        try {
            const groupUpdate = {};
            if (updates.title !== undefined)
                groupUpdate.title = updates.title;
            if (updates.color !== undefined)
                groupUpdate.color = updates.color;
            if (updates.position !== undefined)
                groupUpdate.position = updates.position;
            const { data, error } = await supabase
                .from('board_groups')
                .update(groupUpdate)
                .eq('id', groupId)
                .select()
                .single();
            if (error) {
                console.error('Error updating group:', error);
                return null;
            }
            return {
                id: data.id,
                title: data.title,
                color: data.color,
                position: data.position,
                tasks: updates.tasks || []
            };
        }
        catch (error) {
            console.error('Error in updateGroup:', error);
            return null;
        }
    }
    async deleteGroup(boardId, groupId) {
        try {
            const { error } = await supabase
                .from('board_groups')
                .delete()
                .eq('id', groupId);
            if (error) {
                console.error('Error deleting group:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in deleteGroup:', error);
            return false;
        }
    }
    // ============================================================================
    // TASK OPERATIONS (Future - will implement in next phase)
    // ============================================================================
    async createTask(boardId, groupId, taskData) {
        try {
            // Get current user
            const { data: { user } } = await supabase.auth.getUser();
            if (!user)
                throw new Error('User not authenticated');
            // Prepare task data for insertion
            const taskInsert = {
                board_id: boardId,
                group_id: groupId,
                title: taskData.title || 'New Item',
                status: taskData.status || 'Not Started',
                priority: taskData.priority || 'Medium',
                due_date: taskData.dueDate ? new Date(taskData.dueDate).toISOString() : null,
                assignee_ids: taskData.assigneeIds || [],
                agent_ids: null,
                progress: taskData.progress || 0,
                updated_by_user_id: user.id,
                custom_fields: taskData.customFields || null,
                automation_config: null
            };
            // Insert task into Supabase
            const { data, error } = await supabase
                .from('board_items')
                .insert(taskInsert)
                .select()
                .single();
            if (error) {
                console.error('Error creating task:', error);
                return null;
            }
            // Transform and return the created task
            return {
                id: data.id,
                title: data.title,
                status: data.status,
                priority: data.priority,
                dueDate: data.due_date ? new Date(data.due_date).getTime() : undefined,
                assigneeIds: data.assignee_ids || [],
                progress: data.progress || 0,
                updatedBy: {
                    date: new Date(data.updated_at).getTime(),
                    userId: data.updated_by_user_id,
                    userAvatar: ''
                },
                comments: [],
                customFields: data.custom_fields || {}
            };
        }
        catch (error) {
            console.error('Error in createTask:', error);
            return null;
        }
    }
    async updateTask(boardId, groupId, taskId, updates) {
        try {
            // Get current user for updated_by_user_id
            const { data: { user } } = await supabase.auth.getUser();
            if (!user)
                throw new Error('User not authenticated');
            // Build update object from partial updates
            const taskUpdate = {
                updated_by_user_id: user.id
            };
            if (updates.title !== undefined)
                taskUpdate.title = updates.title;
            if (updates.status !== undefined)
                taskUpdate.status = updates.status;
            if (updates.priority !== undefined)
                taskUpdate.priority = updates.priority;
            if (updates.dueDate !== undefined)
                taskUpdate.due_date = updates.dueDate ? new Date(updates.dueDate).toISOString() : null;
            if (updates.assigneeIds !== undefined)
                taskUpdate.assignee_ids = updates.assigneeIds;
            if (updates.progress !== undefined)
                taskUpdate.progress = updates.progress;
            if (updates.customFields !== undefined)
                taskUpdate.custom_fields = updates.customFields;
            // Update task in Supabase
            const { data, error } = await supabase
                .from('board_items')
                .update(taskUpdate)
                .eq('id', taskId)
                .select()
                .single();
            if (error) {
                console.error('Error updating task:', error);
                return null;
            }
            // Transform and return updated task
            return {
                id: data.id,
                title: data.title,
                status: data.status,
                priority: data.priority,
                dueDate: data.due_date ? new Date(data.due_date).getTime() : undefined,
                assigneeIds: data.assignee_ids || [],
                progress: data.progress || 0,
                updatedBy: {
                    date: new Date(data.updated_at).getTime(),
                    userId: data.updated_by_user_id,
                    userAvatar: ''
                },
                comments: [],
                customFields: data.custom_fields || {}
            };
        }
        catch (error) {
            console.error('Error in updateTask:', error);
            return null;
        }
    }
    async deleteTask(boardId, groupId, taskId) {
        try {
            const { error } = await supabase
                .from('board_items')
                .delete()
                .eq('id', taskId);
            if (error) {
                console.error('Error deleting task:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in deleteTask:', error);
            return false;
        }
    }
    // ============================================================================
    // FLEXI BOARD METHODS
    // ============================================================================
    async getFlexiBoardById(boardId) {
        return this.flexiBoards.get(boardId) || null;
    }
    async updateFlexiBoard(boardId, updatedBoard) {
        const existing = this.flexiBoards.get(boardId);
        if (existing) {
            this.flexiBoards.set(boardId, { ...existing, board: updatedBoard });
        }
    }
    // ============================================================================
    // UTILITY METHODS
    // ============================================================================
    getEmptyBoard = createEmptyBoard;
    getEmptyGroup = createEmptyGroup;
    getEmptyTask = createEmptyTask;
    getEmptyComment = createEmptyComment;
    getDefaultFilter = () => ({
        title: '',
        assigneeId: '',
        status: [],
        priority: [],
        dateRange: undefined
    });
    /**
     * Filter and search boards/tasks
     */
    getFilteredTasks(board, filter) {
        const filteredBoard = { ...board };
        filteredBoard.groups = board.groups.map(group => ({
            ...group,
            tasks: group.tasks.filter(task => {
                // Title filter
                if (filter.title) {
                    const regex = new RegExp(filter.title, 'i');
                    if (!regex.test(task.title))
                        return false;
                }
                // Assignee filter
                if (filter.assigneeId && !task.assigneeIds.includes(filter.assigneeId)) {
                    return false;
                }
                // Status filter
                if (filter.status.length > 0 && !filter.status.includes(task.status)) {
                    return false;
                }
                // Priority filter
                if (filter.priority.length > 0 && !filter.priority.includes(task.priority)) {
                    return false;
                }
                // Date range filter
                if (filter.dateRange && task.dueDate) {
                    const taskDate = new Date(task.dueDate);
                    if (taskDate < filter.dateRange.start || taskDate > filter.dateRange.end) {
                        return false;
                    }
                }
                return true;
            })
        }));
        return filteredBoard;
    }
    // ============================================================================
    // BOARD MEMBERS OPERATIONS
    // ============================================================================
    /**
     * Get all members of a board
     */
    async getBoardMembers(boardId) {
        try {
            const { data, error } = await supabase
                .from('board_members')
                .select('*')
                .eq('board_id', boardId);
            if (error) {
                console.error('[BoardService] Error fetching board members:', error);
                return [];
            }
            return data || [];
        }
        catch (error) {
            console.error('[BoardService] Failed to fetch board members:', error);
            return [];
        }
    }
    /**
     * Add a member to a board
     */
    async addBoardMember(boardId, userId, userName, userAvatar = null, role = 'member') {
        try {
            const { data, error } = await supabase
                .from('board_members')
                .insert({
                board_id: boardId,
                user_id: userId,
                user_name: userName,
                user_avatar: userAvatar,
                role
            })
                .select()
                .single();
            if (error) {
                console.error('[BoardService] Error adding board member:', error);
                return null;
            }
            return data;
        }
        catch (error) {
            console.error('[BoardService] Failed to add board member:', error);
            return null;
        }
    }
    /**
     * Update a board member's role
     */
    async updateBoardMemberRole(boardId, userId, role) {
        try {
            const { error } = await supabase
                .from('board_members')
                .update({ role })
                .eq('board_id', boardId)
                .eq('user_id', userId);
            if (error) {
                console.error('[BoardService] Error updating board member role:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('[BoardService] Failed to update board member role:', error);
            return false;
        }
    }
    /**
     * Remove a member from a board
     */
    async removeBoardMember(boardId, userId) {
        try {
            const { error } = await supabase
                .from('board_members')
                .delete()
                .eq('board_id', boardId)
                .eq('user_id', userId);
            if (error) {
                console.error('[BoardService] Error removing board member:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('[BoardService] Failed to remove board member:', error);
            return false;
        }
    }
    /**
     * Check if user is board owner
     */
    async isBoardOwner(boardId, userId) {
        try {
            const { data, error } = await supabase
                .from('board_members')
                .select('role')
                .eq('board_id', boardId)
                .eq('user_id', userId)
                .single();
            if (error || !data) {
                return false;
            }
            return data.role === 'owner';
        }
        catch (error) {
            console.error('[BoardService] Failed to check board owner:', error);
            return false;
        }
    }
    // ============================================================================
    // BOARD NOTIFICATIONS OPERATIONS
    // ============================================================================
    /**
     * Get notification preferences for a user on a board
     */
    async getBoardNotificationPreferences(boardId, userId) {
        try {
            const { data, error } = await supabase
                .from('board_notifications')
                .select('*')
                .eq('board_id', boardId)
                .eq('user_id', userId)
                .single();
            if (error) {
                if (error.code === 'PGRST116') {
                    // No row found - return defaults
                    return {
                        board_id: boardId,
                        user_id: userId,
                        is_global_muted: false,
                        mentioned_enabled: true,
                        assigned_enabled: true,
                        automations_enabled: true
                    };
                }
                console.error('[BoardService] Error fetching notification preferences:', error);
                return null;
            }
            return data;
        }
        catch (error) {
            console.error('[BoardService] Failed to fetch notification preferences:', error);
            return null;
        }
    }
    /**
     * Create or update notification preferences for a user on a board
     */
    async setBoardNotificationPreferences(boardId, userId, preferences) {
        try {
            const { data, error } = await supabase
                .from('board_notifications')
                .upsert({
                board_id: boardId,
                user_id: userId,
                ...preferences
            })
                .select()
                .single();
            if (error) {
                console.error('[BoardService] Error setting notification preferences:', error);
                return null;
            }
            return data;
        }
        catch (error) {
            console.error('[BoardService] Failed to set notification preferences:', error);
            return null;
        }
    }
    /**
     * Toggle global mute for notifications on a board
     */
    async toggleBoardNotificationMute(boardId, userId) {
        try {
            // Get current preference
            const current = await this.getBoardNotificationPreferences(boardId, userId);
            if (!current)
                return false;
            // Toggle and update
            const updated = await this.setBoardNotificationPreferences(boardId, userId, {
                is_global_muted: !current.is_global_muted
            });
            return updated !== null;
        }
        catch (error) {
            console.error('[BoardService] Failed to toggle notification mute:', error);
            return false;
        }
    }
    /**
     * Update specific notification type preference
     */
    async updateNotificationPreference(boardId, userId, preferenceType, enabled) {
        try {
            const { error } = await supabase
                .from('board_notifications')
                .update({ [preferenceType]: enabled })
                .eq('board_id', boardId)
                .eq('user_id', userId);
            if (error) {
                console.error('[BoardService] Error updating notification preference:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('[BoardService] Failed to update notification preference:', error);
            return false;
        }
    }
    /**
     * Send an invitation to a user to join a board
     */
    async inviteBoardMember(boardId, userId, invitedByUserId, boardTitle, role = 'member') {
        try {
            // Create invitation record
            const { data: invitation, error: invError } = await supabase
                .from('board_invitations')
                .insert({
                board_id: boardId,
                user_id: userId,
                invited_by: invitedByUserId,
                role,
                status: 'pending',
                created_at: new Date().toISOString()
            })
                .select()
                .single();
            if (invError) {
                console.error('[BoardService] Error creating invitation:', invError);
                return null;
            }
            // Create notification for the invited user
            const { error: notifError } = await supabase
                .from('notifications')
                .insert({
                user_id: userId,
                type: 'board_invitation',
                title: `Board Invitation`,
                message: `You have been invited to join "${boardTitle}" board as a ${role}`,
                data: {
                    board_id: boardId,
                    board_title: boardTitle,
                    invitation_id: invitation.id,
                    invited_by: invitedByUserId,
                    role
                },
                read: false,
                created_at: new Date().toISOString()
            });
            if (notifError) {
                console.error('[BoardService] Error creating notification:', notifError);
            }
            return invitation;
        }
        catch (error) {
            console.error('[BoardService] Failed to invite board member:', error);
            return null;
        }
    }
    /**
     * Accept a board invitation
     */
    async acceptBoardInvitation(invitationId, userId) {
        try {
            // Get the invitation
            const { data: invitation, error: getError } = await supabase
                .from('board_invitations')
                .select('*')
                .eq('id', invitationId)
                .eq('user_id', userId)
                .single();
            if (getError || !invitation) {
                console.error('[BoardService] Error fetching invitation:', getError);
                return false;
            }
            // Add user as board member
            const { error: memberError } = await supabase
                .from('board_members')
                .insert({
                board_id: invitation.board_id,
                user_id: userId,
                role: invitation.role
            });
            if (memberError) {
                console.error('[BoardService] Error adding board member:', memberError);
                return false;
            }
            // Update invitation status
            const { error: updateError } = await supabase
                .from('board_invitations')
                .update({ status: 'accepted' })
                .eq('id', invitationId);
            if (updateError) {
                console.error('[BoardService] Error updating invitation:', updateError);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('[BoardService] Failed to accept invitation:', error);
            return false;
        }
    }
    /**
     * Reject a board invitation
     */
    async rejectBoardInvitation(invitationId, userId) {
        try {
            const { error } = await supabase
                .from('board_invitations')
                .update({ status: 'rejected' })
                .eq('id', invitationId)
                .eq('user_id', userId);
            if (error) {
                console.error('[BoardService] Error rejecting invitation:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('[BoardService] Failed to reject invitation:', error);
            return false;
        }
    }
}
export const boardSupabaseService = new BoardSupabaseService();
export default boardSupabaseService;
